import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProgressData } from '../hooks/useProgressData';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import CrackedScorePanel from '../components/progress/CrackedScorePanel';
import ConfusionBacklogPanel from '../components/progress/ConfusionBacklogPanel';
import RevisitQueuePanel from '../components/progress/RevisitQueuePanel';
import SubjectSnapshotGrid from '../components/progress/SubjectSnapshotGrid';
import { ChevronLeft, Settings, LogOut, BarChart3 } from 'lucide-react';

export default function ProgressPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    loading,
    usingMock,
    insights,
    confusionLogs,
    subjects,
    lectures,
    reviewSessions,
    studyDashboard,
    cracked,
  } = useProgressData();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      <header className="glass sticky top-0 z-50 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="flex-shrink-0" asChild>
              <Link to="/dashboard" aria-label="Back to dashboard">
                <ChevronLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <BarChart3 className="w-5 h-5 text-primary flex-shrink-0 hidden sm:block" aria-hidden />
              <div className="min-w-0">
                <h1 className="font-serif text-lg sm:text-xl text-foreground truncate leading-tight">
                  Progress
                </h1>
                <p className="text-[11px] text-muted-foreground truncate hidden sm:block">
                  Mastery, confusions, and where to study next
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <span className="text-xs text-muted-foreground max-w-[120px] truncate hidden md:inline">
              {user?.name || user?.email}
            </span>
            <Link to="/settings">
              <Button variant="ghost" size="icon" aria-label="Settings">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Log out">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 space-y-4">
              <Skeleton className="h-48 w-full rounded-xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
            <div className="lg:col-span-7 space-y-4">
              <Skeleton className="h-72 w-full rounded-xl" />
              <Skeleton className="h-56 w-full rounded-xl" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 space-y-6">
              <CrackedScorePanel cracked={cracked} usingMock={usingMock} />
              <ConfusionBacklogPanel logs={confusionLogs} />
            </div>
            <div className="lg:col-span-7 space-y-6">
              <RevisitQueuePanel insights={insights} reviewSessions={reviewSessions} />
              <SubjectSnapshotGrid
                subjects={subjects}
                lectures={lectures}
                studyDashboard={studyDashboard}
                insights={insights}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
